<script setup lang="ts">
import uniqBy from 'lodash.uniqby';
import {
  computed,
  defineOgImageComponent,
  onMounted,
  ref,
  useAsyncData,
  useCreators,
  useHead,
  useMetrics,
  useRoute,
  useRouter,
  watch,
  useDialog,
  useToast,
  useAuth,
} from '#imports';

import { useBreakpoints } from '@vueuse/core';
const { currentUser } = useAuth();

const breakpoints = useBreakpoints(
  { mobile: 920 },
  { ssrWidth: 920 }
)
const isMobile = breakpoints.smaller('mobile');

const { openDialog, setDialogFullscreen } = useDialog();
const { showError } = useToast();
const { trackEvent } = useMetrics();

// Fetch Creator
const route = useRoute();
const handle = route.params.handle as string;
const { fetchCreatorByHandle } = useCreators();
const {
  data: creator,
  pending: creatorPending,
  error: creatorError,
} = await useAsyncData(`creator-${handle}`, () => fetchCreatorByHandle(handle));
// Open Social Links in New Tab
const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const isSupporter = computed(() => {
  return currentUser.value?.supports?.some(
    (support) => support.creator_id === creator.value?.id,
  ) ?? false;
});

useHead(() => ({
  title: `${creator.value.display_name} - @${creator.value.mappr_handle}`,
  meta: [
    {
      name: 'description',
      content: creator.value.summary,
    },
  ],
}));

defineOgImageComponent('Default', {
  title: `${creator.value.display_name} - @${creator.value.mappr_handle}`,
  description: creator.value.summary,
  imagePath: creator.value.profile_image_url,
});

// On Load fix issue where page starts scrolled half way
onMounted(() => {
  window.scrollTo(0, 0);
  if (creator.value) {
    trackEvent('viewed_profile', {
      creator_handle: creator.value.mappr_handle,
      creator_id: creator.value.id,
    });
  }
});

const categories = computed(() => {
  if (!creator.value || !creator.value.maps) return [];
  const allCategories = creator.value.maps.flatMap((map) =>
    map.spots.flatMap((spot) => spot.categories),
  );
  // Filter out null values
  const nonNullCategories = allCategories.filter(
    (category) => category != null,
  );
  return uniqBy(nonNullCategories, 'id');
});

const router = useRouter();
const handleBackButton = () => {
  router.back();
};

const openShareDialog = () => {
  if (!creator.value) return;
  try {
    openDialog('MPShare', {
      creator: creator.value,
      subtitle: 'Share:',
      version: 'creator',
    });
  } catch (e) {
    showError('Error opening share dialog');
  }
};

type PricingTier = 'premium' | 'support';
const pricingTier = ref<PricingTier>('premium');
const openPricingDialog = (tier: PricingTier) => {
  if (!creator.value) return;
  pricingTier.value = tier;
  openDialog('MPPricing', {
    isOpen: 'pricingDialogIsOpen',
    creator: creator,
    showSupporterTier: creator.stripe_account_id,
    hideSubscriberTier: true

  });
  setDialogFullscreen(true);
  trackEvent(`${tier}_intent`, {
    creator_id: creator.value.id,
    creator_handle: creator.value.mappr_handle,
  });
};
onMounted(() => {
  if (import.meta.client && window) {
      window.history.scrollRestoration = 'auto';
  }
});

</script>

<template>
  <main class="creator-profile-page">
    <div v-if="creatorPending"/>
    <div v-if="creatorError && !creatorPending" class="profile-page">
      Could not find creator
    </div>
    <section class="hero" v-if="creator">
      <div class="hero-container">
        <article class="hero-bio">
          <small v-if="!isMobile" class="bio-handle">
          @{{ creator.mappr_handle}}
          </small>
          <h2 class="bio-name" v-if="!isMobile">{{creator.display_name}} </h2>
          <p v-if="creator.based_in" class="bio-based"><span>Based: </span> Everywhere</p>
          <p class="bio-focus-categories" v-if="categories && categories.length > 2">
            <span>Focuses on:</span>
            <span v-for="category in categories.slice(0, 2)">
              <i :class="category.icon"/>
              {{category.name}}
            </span>
          </p>
          <hr />
          <p class="bio-text">
            {{creator.bio_text}}
          </p>
          <div class="actions-container" v-if="!isMobile">
            <MPButton
              v-show="!isSupporter && creator.stripe_account_id"
              label="Support"
              class="mobile-support-button"
              version="primary"
              size="medium"
              rounded
              @click="openPricingDialog('support')"
            />
            <MPFollow :creator="creator" size="medium" bg-color="var(--color-slate-200)" />
            <MPButton
              icon="ri-share-line"
              label="Share"
              size="medium"
              hide-label
              rounded
              version="secondary"
              icon-color="var(--color-slate-400)"
              @click="openShareDialog()"
            />
            <div class="social-links">
              <MPButton
                v-if="creator.tiktok_id"
                icon="ri-tiktok-line"
                label="tiktok"
                size="medium"
                hide-label
                rounded
                version="secondary"
                icon-color="var(--color-slate-400)"
                @click="openInNewTab(`https://www.tiktok.com/@${creator.tiktok_id}`)"
              />
              <MPButton
                v-if="creator.facebook_id"
                icon="ri-facebook-fill"
                label="facebook"
                hide-label
                size="medium"
                rounded
                version="secondary"
                icon-color="var(--color-slate-400)"
                @click="openInNewTab(`https://facebook.com/@${creator.facebook_id}`)"
              />
              <MPButton
                v-if="creator.instagram_id"
                icon="ri-instagram-line"
                label="instagram"
                hide-label
                size="medium"
                rounded
                version="secondary"
                icon-color="var(--color-slate-400)"
                @click="openInNewTab(`https://instagram.com/${creator.instagram_id}`)"
              />
              <MPButton
                v-if="creator.twitter_id"
                icon="ri-twitter-x-line"
                label="twitter/x"
                hide-label
                size="medium"
                rounded
                version="secondary"
                icon-color="var(--color-slate-400)"
                @click="openInNewTab(`https://twitter.com/${creator.twitter_id}`)"
              />
              <MPButton
                v-if="creator.youtube_id"
                icon="ri-youtube-fill"
                label="youtube"
                hide-label
                size="medium"
                rounded
                version="secondary"
                icon-color="var(--color-slate-400)"
                @click="openInNewTab(`https://youtube.com/@${creator.youtube_id}`)"
              />
            </div>
          </div>
        </article>
        <article class="hero-image">
          <MPButton
            v-if="isMobile"
            icon="ri-arrow-left-s-line"
            hide-label
            label="back"
            version="link"
            class="back-button"
            rounded
            background-color="transparent"
            icon-color="white"
            @click="handleBackButton"
          />
          <div class="social-links" v-if="isMobile">
            <MPButton
              v-if="creator.tiktok_id"
              icon="ri-tiktok-line"
              label="tiktok"
              hide-label
              size="small"
              version="secondary"
              background-color="hsla(0, 0%, 68%, 0.5)"
              icon-color="white"
              :outline="false"
              @click="openInNewTab(`https://www.tiktok.com/@${creator.tiktok_id}`)"
            />
            <MPButton
              v-if="creator.facebook_id"
              icon="ri-facebook-fill"
              label="facebook"
              hide-label
              size="small"
              version="secondary"
              background-color="hsla(0, 0%, 68%, 0.5)"
              icon-color="white"
              :outline="false"
              @click="openInNewTab(`https://facebook.com/@${creator.facebook_id}`)"
            />
            <MPButton
              v-if="creator.instagram_id"
              icon="ri-instagram-line"
              label="instagram"
              hide-label
              size="small"
              version="secondary"
              background-color="hsla(0, 0%, 68%, 0.5)"
              icon-color="white"
              :outline="false"
              @click="openInNewTab(`https://instagram.com/${creator.instagram_id}`)"
            />
            <MPButton
              v-if="creator.twitter_id"
              icon="ri-twitter-x-line"
              label="twitter/x"
              hide-label
              size="small"
              version="secondary"
              background-color="hsla(0, 0%, 68%, 0.5)"
              icon-color="white"
              :outline="false"
              @click="openInNewTab(`https://twitter.com/${creator.twitter_id}`)"
            />
            <MPButton
              v-if="creator.youtube_id"
              icon="ri-youtube-fill"
              label="youtube"
              hide-label
              size="small"
              version="secondary"
              background-color="hsla(0, 0%, 68%, 0.5)"
              icon-color="white"
              :outline="false"
              @click="openInNewTab(`https://youtube.com/@${creator.youtube_id}`)"
            />
          </div>
          <div class="image-container">
            <MPImg :src="creator.profile_image_url" :width="800" />
          </div>
          <div class="image-overlay" v-if="isMobile">
            <div class="overlay-bio">
              <h4 class="overlay-bio-name">{{creator.display_name}} </h4>
              <small class="overlay-bio-handle">
              @{{ creator.mappr_handle}}
              </small>
            </div>
            <div class="actions-container">
              <MPButton
                v-show="!isSupporter && creator.stripe_account_id"
                label="Support"
                class="mobile-support-button"
                version="primary"
                size="small"
                rounded
                @click="openPricingDialog('support')"
              />
              <MPFollow :creator="creator" size="small" bg-color="var(--color-slate-200)" />
              <MPButton
                icon="ri-share-line"
                version="secondary"
                size="small"
                rounded
                link
                hide-label
                background-color="var(--color-slate-100)"
                @click="openShareDialog()"
              />
            </div>
          </div>
        </article>
      </div>
    </section>
    <section class="creator-profile-content">
      <header>
        <menu class="creator-profile-content-menu">
          <!-- <li>
            <NuxtLinkLocale
              no-scroll
              :to="`/@${handle}`"
              exactActiveClass="active"
            >
              <i class="ri-layout-grid-fill" />
              All
            </NuxtLinkLocale>
          </li> -->
          <li>
            <NuxtLinkLocale
              no-scroll
              :to="`/@${handle}`"
              exactActiveClass="active"
            >
              <i class="ri-map-pin-fill" />
              Spots
            </NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale
              no-scroll
              :to="`/@${handle}/maps`"
              exactActiveClass="active"
            >
              <i class="ri-map-2-fill" />
              Maps
            </NuxtLinkLocale>
          </li>
          <!--
          Disabled until functionality exists
          <li>
            <NuxtLinkLocale :to="`/creators/${handle}/posts`">
              Posts
            </NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale :to="`/creators/${handle}/updates`">
              Updates
            </NuxtLinkLocale>
          </li> -->
        </menu>
      </header>
      <main class="page-container">
        <NuxtPage :creator="creator" />
      </main>
    </section>
  </main>
</template>
<style lang="postcss" scoped>
@import '@/assets/css/layouts.pcss';
@import '@/assets/css/breakpoints.pcss';
@import '@/assets/css/typography.pcss';

.creator-profile-page {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  height: 100%;
  z-index: 8100;
  background-color: white;

  @mixin screen md {
    position: relative;
    z-index: 0;
  }
}

.hero {
  width: 100%;

  @mixin screen md {
    background-color: var(--color-slate-50);
  }
}

.hero-container {
  display: grid;
  grid-template-columns: 1fr;

  @mixin screen md {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    @mixin layout-wrapper-wide;
    padding-block: 2rem;
  }
}

.hero-bio {
  padding-inline: 1rem;
  padding-block: 1rem;

  @mixin screen md {
    padding-inline: 0;
    padding-block: 2rem;
    align-content: center;
  }

  hr {
    border: 1px solid var(--color-slate-300);

    @mixin screen md {
      width: 70%
    }
  }
}

.hero-bio small {
  color: var(--color-rose-500);
}

.hero-bio h2 {
  margin-top: 0.5rem;
}

.hero-bio .bio-based {
  font-family: var(--font-families-uncut-sans);
  color: var(--color-slate-500);
  margin-bottom: 0;
}

.hero-bio .bio-focus-categories {
  font-family: var(--font-families-uncut-sans);
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  color: var(--color-slate-500);
}

.hero-bio .bio-text {
  font-family: var(--font-families-uncut-sans);
  color: var(--color-slate-700)
}

.hero-bio .actions-container {
  display: flex;
  gap: 0.5rem;
  padding-block: 1rem;
  :deep(button) {
    padding-inline: 1.75rem;
  }

}
.hero-bio .actions-container .social-links {
  display: flex;
  gap: 0.5rem;
}

.hero-image {
  width: 100%;
  justify-content: center end;
  display: flex;
  position: relative;
  grid-row: 1;

  @mixin screen md {
    grid-row: auto;
    display: grid;
    min-height: 100%;

    &::before {
      display: none;
    }
  }
}

.hero-image .image-container {
  width: 100%;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2) 40%, rgba(0, 0, 0, 0.9));
    pointer-events: none;
    z-index: 1;
  }

  img {
    width: 100%;
  }

  @mixin screen md {
    border-radius: 50%;
    height: 75%;
    aspect-ratio: 1;
    place-self: center end;
    overflow: hidden;
    width: auto;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: transparent;
      pointer-events: none;
      z-index: 1;
    }
    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }

}

.hero-image .back-button {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  :deep(i) {
    font-size: 2rem !important;
  }
}

.hero-image .image-overlay {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  width: 100%;
  z-index: 10;
  .overlay-bio {
    margin-bottom: 1rem;
  }

  .overlay-bio-handle {
    color: white;
    margin: 0;
  }
  .overlay-bio-name {
    color: white;
    margin: 0;
    margin-bottom: 0.5rem;
  }
  @mixin screen md {
    display: none;
  }
}

.hero-image .image-overlay .actions-container {
  display: flex;
  gap: 0.5rem;
  bottom: 1rem;
  left: 0.5rem;

  :deep(button) {
    padding-inline: 1.75rem;
  }

}
.hero-image .social-links {
  position: absolute;
  display: flex;
  gap: 0.5rem;
  top: 0.5rem;
  right: 1rem;
  button {
    border: none !important;
    border-radius: 0.75rem;
  }

  :deep(i) {
    font-size: 1.5rem !important;
    padding: 0.20rem 0.35rem
  }

  @mixin screen md {
    display: none;
  }
}

.creator-profile-content {
  padding-inline: 1rem;
  @mixin screen md {
    @mixin layout-wrapper-wide;
  }

}

.creator-profile-content-menu {
 margin: 0;
 padding: 0;
 padding-block: 2rem;
 list-style: none;
 display: flex;
 gap: 0.5rem;
  li {
    list-style: none;
  }
  i {
    color: var(--color-slate-400);
  }
}

.creator-profile-content-menu li a {
  background-color: var(--color-slate-50);
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  text-decoration: none;
  color: var(--color-slate-900);
  transition: background-color 0.2s ease-in-out;
}
.creator-profile-content-menu li .active {
  background-color: var(--color-rose-400);
  color: var(--color-white);
  i {
    color: var(--color-white);
  }
}

.page-container {
  margin-bottom: 5rem;
}

</style>
